import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Link, InputAdornment } from '@mui/material';
import { EmailOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from "../../services/apiService";
import { toast } from "react-toastify";

export function PasswordForm() {
  const [formData, setFormData] = useState({ username: '' });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validatePassword = () => {
    const newErrors = {};
    if (!formData.username) {
      newErrors.username = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.username = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePassword = async (event) => {
    event.preventDefault();
    if (!validatePassword()) return;
    try {
      await requestPasswordReset(formData.username);
      toast.success('Password reset link sent successfully!', { autoClose: 2000 });
      navigate("/login");
    } catch (error) {
      toast.error("Failed to send password reset email. Please try again.");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: { xs: '1.3125rem', sm: '1.3125rem', md: '1.3125rem' },
          textAlign: 'center',
          mb: { xs: 0.75, sm: 0.875, md: 0.875 },
          color: '#383838',
          lineHeight: '100%',
        }}
      >
        Forget your password?
      </Typography>

      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontWeight: 400,
          fontSize: { xs: '0.84375rem', sm: '0.84375rem', md: '0.84375rem' },
          textAlign: 'center',
          mb: { xs: 1.75, sm: 2, md: 2 },
          color: '#4A5565',
          lineHeight: '1.5rem',
          letterSpacing: '-0.31px',
          px: { xs: 1, sm: 0 },
        }}
      >
        Enter your email and we&apos;ll send you a link to reset your password.
      </Typography>

      <Box
        component="form"
        onSubmit={handlePassword}
        sx={{ mt: { xs: 0.5, sm: 0.5, md: 0.5 } }}
      >
        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            Email
          </Typography>
          <TextField
            fullWidth
            name="username"
            placeholder="you@example.com"
            value={formData.username}
            onChange={handleChange}
            error={!!errors.username}
            helperText={errors.username}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start"
                  sx={{
                    minWidth: { xs: '40px', sm: '48px', md: '48px' },
                    mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <EmailOutlined
                    sx={{
                      color: '#9CA3AF',
                      fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            height: { xs: '44px', sm: '44px', md: '46px' },
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: { xs: '0.90625rem', sm: '0.84375rem', md: '0.84375rem' },
            mb: { xs: 2, sm: 2, md: 2 },
            background: '#0F172A',
            color: '#ffffff',
            textTransform: 'none',
            boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.2)',
            '&:hover': {
              background: '#1E293B',
              boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.3)',
            },
            '&:active': {
              background: '#0A0F1A',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Send Reset Link
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            onClick={() => navigate('/login')}
            sx={{
              fontFamily: '"Open Sans", sans-serif',
              fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
              lineHeight: '22px',
              letterSpacing: '0%',
              cursor: 'pointer',
              color: '#0F172A',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-block',
              verticalAlign: 'middle',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1E293B',
              },
              transition: 'color 0.2s ease',
            }}
          >
            Back
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

export default PasswordForm;
